// pages/reports/TrainingListAttendance.js
import React, { useState } from 'react';
import { Button, Form, Spinner, Table } from 'react-bootstrap';
import ReportFilterBar from '../../components/reports/ReportFilterBar';
import useReportData from '../../hooks/useReportData';
import useReportFilters from '../../hooks/useReportFilters';
import { getUser } from '../../utils/auth';

const COMPANY_HIERARCHY_ROLES = ['masteradmin', 'admin', 'companyadmin', 'departmentadmin'];

function TrainingListAttendance() {
  const loggedInUser = getUser();
  const isCompanyHierarchy = COMPANY_HIERARCHY_ROLES.includes(loggedInUser?.role);
  const isVendorAdmin = loggedInUser?.role === 'trainingadmin';

  const [filters, setFilters] = useState(null);
  const [trainerId, setTrainerId] = useState('');

  // Only the company-hierarchy branch uses the department filter dropdown —
  // enabled=false for the other roles skips a call that's a designed-in 403
  // for them (get_report_filters.php only recognizes those 3 roles). The
  // department filter's value itself lives in filters.departmentId (set by
  // ReportFilterBar when showDepartmentFilter is on) — useReportData already
  // reads that natively, so only the trainer filter (a dimension it doesn't
  // know about) needs to go through extraParams.
  const { departments } = useReportFilters(isCompanyHierarchy);

  const extraParams = isVendorAdmin ? { trainer_id: trainerId } : {};

  const { data, loading, error, exportUrl } = useReportData(
    'reports/get_training_list_attendance.php',
    filters,
    extraParams
  );

  const trainerOptions = data?.trainerOptions ?? [];

  return (
    <div className="p-2">
      <div className="d-flex justify-content-between align-items-center no-print">
        <h2>Training List with Attendance Counts</h2>
        <div>
          <Button variant="outline-secondary" className="me-2" onClick={() => window.print()} disabled={!data}>
            Print
          </Button>
          {exportUrl && (
            <Button as="a" href={exportUrl} variant="outline-primary">
              Export to CSV
            </Button>
          )}
        </div>
      </div>

      <ReportFilterBar
        value={filters}
        onChange={setFilters}
        showDepartmentFilter={isCompanyHierarchy}
        departments={departments}
      />

      {isVendorAdmin && (
        <Form.Group className="mb-3 no-print" style={{ maxWidth: 300 }}>
          <Form.Label>Trainer</Form.Label>
          <Form.Control as="select" value={trainerId} onChange={(e) => setTrainerId(e.target.value)}>
            <option value="">All Trainers</option>
            {trainerOptions.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </Form.Control>
        </Form.Group>
      )}

      {error && <div className="alert alert-danger">{error}</div>}
      {loading && <Spinner animation="border" size="sm" />}

      {data && !loading && (
        <>
          <h5 className="text-muted">{data.start} to {data.end}</h5>
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th>Code</th>
                <th>Title</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Trainer</th>
                <th>Attendance Count</th>
              </tr>
            </thead>
            <tbody>
              {data.trainings.length > 0 ? data.trainings.map(t => (
                <tr key={t.id}>
                  <td>{t.code}</td>
                  <td>{t.title}</td>
                  <td>{t.startDate}</td>
                  <td>{t.endDate}</td>
                  <td>{t.trainer}</td>
                  <td>{t.attendanceCount}</td>
                </tr>
              )) : (
                <tr><td colSpan="6" className="text-center">No trainings found</td></tr>
              )}
            </tbody>
          </Table>
        </>
      )}
    </div>
  );
}

export default TrainingListAttendance;
